import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import {
  getAIProviderChain,
  testAIProviderChainSlot,
  updateAIProviderChain,
} from '../../services/api.admin';
import { getApiErrorMessage } from '../../lib/apiErrors';

const PURPOSES = {
  market_analysis: 'Market Analysis',
  market_sentiment: 'Market Sentiment',
  ai_guided_trading: 'AI Research Trader',
};
const ROLES = { primary: 'Primary LLM', secondary: 'Secondary LLM', fallback: 'Final Fallback' };
const PRESETS = {
  ollama_gemma: { provider_label: 'Local Ollama - Gemma 4 e4b', mode: 'ollama', model: 'ollama/gemma4:e4b', api_base: 'http://host.docker.internal:11434', api_key_env: '' },
  anthropic_fable: { provider_label: 'Anthropic Claude Fable 5', mode: 'custom', model: 'anthropic/claude-fable-5', api_base: '', api_key_env: 'ANTHROPIC_API_KEY' },
  anthropic_sonnet: { provider_label: 'Anthropic Claude Sonnet 5', mode: 'custom', model: 'anthropic/claude-sonnet-5', api_base: '', api_key_env: 'ANTHROPIC_API_KEY' },
  openai_frontier: { provider_label: 'OpenAI GPT-5.5', mode: 'openai_compatible', model: 'gpt-5.5', api_base: '', api_key_env: 'OPENAI_API_KEY' },
  openai_latest: { provider_label: 'OpenAI GPT-5.6', mode: 'openai_compatible', model: 'gpt-5.6', api_base: '', api_key_env: 'OPENAI_API_KEY' },
  custom: { provider_label: 'Custom Provider', mode: 'custom', model: '', api_base: '', api_key_env: '' },
};

const detectPreset = (provider) => {
  const model = String(provider?.model || '').toLowerCase();
  if (model === 'ollama/gemma4:e4b') return 'ollama_gemma';
  if (model.includes('claude-fable-5')) return 'anthropic_fable';
  if (model.includes('claude-sonnet-5')) return 'anthropic_sonnet';
  if (model === 'gpt-5.5') return 'openai_frontier';
  if (model === 'gpt-5.6') return 'openai_latest';
  return 'custom';
};

const normalizeChain = (data, purpose) => ({
  purpose,
  inherit_from: data?.inherit_from || null,
  system_prompt: data?.system_prompt || '',
  providers: ['primary', 'secondary', 'fallback'].map((role) => {
    const found = (data?.providers || []).find((provider) => provider.role === role) || {};
    return { ...found, role, api_key: '', clear_api_key: false };
  }),
});

const AIProviderChainManager = () => {
  const [purpose, setPurpose] = useState('market_analysis');
  const [role, setRole] = useState('primary');
  const [chain, setChain] = useState(() => normalizeChain({}, 'market_analysis'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadChain = useCallback(async (nextPurpose) => {
    setLoading(true);
    setTestResult(null);
    try {
      const { data } = await getAIProviderChain(nextPurpose);
      setChain(normalizeChain(data, nextPurpose));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load AI provider chain'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadChain(purpose); }, [loadChain, purpose]);

  const provider = useMemo(
    () => chain.providers.find((item) => item.role === role) || {},
    [chain.providers, role],
  );
  const presetKey = detectPreset(provider);
  const inherited = purpose === 'market_sentiment' && chain.inherit_from === 'market_analysis';

  const updateProvider = (field, value) => {
    setChain((current) => ({
      ...current,
      providers: current.providers.map((item) => item.role === role ? { ...item, [field]: value } : item),
    }));
    setTestResult(null);
  };

  const applyPreset = (key) => {
    const preset = PRESETS[key];
    if (!preset) return;
    setChain((current) => ({
      ...current,
      providers: current.providers.map((item) => item.role === role ? { ...item, ...preset } : item),
    }));
    setTestResult(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await updateAIProviderChain(purpose, {
        inherit_from: inherited ? 'market_analysis' : null,
        system_prompt: chain.system_prompt,
        providers: chain.providers,
      });
      setChain(normalizeChain(data, purpose));
      toast.success(`${PURPOSES[purpose]} provider chain saved`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save AI provider chain'));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await testAIProviderChainSlot(purpose, role, provider);
      setTestResult(data || { ok: true, message: 'Connection successful' });
    } catch (error) {
      setTestResult({ ok: false, message: getApiErrorMessage(error, 'Connection test failed') });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader>
        <CardTitle>AI Provider Chains</CardTitle>
        <CardDescription>Configure an ordered primary, secondary, and final fallback independently for each AI purpose.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm text-zinc-400">Purpose</label>
            <Select value={purpose} onValueChange={(value) => { setPurpose(value); setRole('primary'); }}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {Object.entries(PURPOSES).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm text-zinc-400">Provider slot</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {Object.entries(ROLES).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {purpose === 'market_sentiment' ? (
          <div className={`flex flex-col gap-3 rounded-md border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${inherited ? 'border-cyan-500/30 bg-cyan-500/10' : 'border-zinc-800 bg-zinc-950/60'}`}>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-sm font-medium text-white">
                  {inherited ? 'Inherited from Market Analysis' : 'Independent Market Sentiment chain'}
                </div>
                <span className={`rounded border px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] ${inherited ? 'border-cyan-400/30 text-cyan-200' : 'border-emerald-400/30 text-emerald-200'}`}>
                  {inherited ? 'Inherited' : 'Independent'}
                </span>
              </div>
              <div className="mt-1 text-xs text-zinc-400">
                {inherited
                  ? 'Sentiment uses the complete Market Analysis provider chain and prompt. There is nothing separate to edit.'
                  : 'Sentiment has its own providers, credentials, failover order, and purpose prompt.'}
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setChain((current) => ({ ...current, inherit_from: inherited ? null : 'market_analysis' }))}
              className="shrink-0"
            >
              {inherited ? 'Configure independently' : 'Use Market Analysis chain'}
            </Button>
          </div>
        ) : null}

        {!inherited ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950/60 px-4 py-3">
            <div><div className="text-sm font-medium text-white">{ROLES[role]}</div><div className="text-xs text-zinc-500">Failover occurs only after technical failure or invalid structured output.</div></div>
            <Switch checked={Boolean(provider.enabled)} onCheckedChange={(checked) => updateProvider('enabled', checked)} />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2"><label className="text-sm text-zinc-400">Provider preset</label><Select value={presetKey} onValueChange={applyPreset}><SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger><SelectContent className="bg-zinc-900 border-zinc-800"><SelectItem value="ollama_gemma">Local Ollama - Gemma</SelectItem><SelectItem value="anthropic_fable">Anthropic Claude Fable 5</SelectItem><SelectItem value="anthropic_sonnet">Anthropic Claude Sonnet 5</SelectItem><SelectItem value="openai_frontier">OpenAI GPT-5.5</SelectItem><SelectItem value="openai_latest">OpenAI GPT-5.6</SelectItem><SelectItem value="custom">Custom</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><label className="text-sm text-zinc-400">Display label</label><Input value={provider.provider_label || ''} onChange={(event) => updateProvider('provider_label', event.target.value)} className="bg-zinc-800 border-zinc-700" /></div>
            <div className="space-y-2"><label className="text-sm text-zinc-400">Model ID</label><Input value={provider.model || ''} onChange={(event) => updateProvider('model', event.target.value)} className="bg-zinc-800 border-zinc-700" /></div>
            <div className="space-y-2"><label className="text-sm text-zinc-400">API base URL</label><Input value={provider.api_base || ''} onChange={(event) => updateProvider('api_base', event.target.value)} placeholder="Provider default" className="bg-zinc-800 border-zinc-700" /></div>
            <div className="space-y-2"><label className="text-sm text-zinc-400">API key env var</label><Input value={provider.api_key_env || ''} onChange={(event) => updateProvider('api_key_env', event.target.value)} className="bg-zinc-800 border-zinc-700" /></div>
            <div className="space-y-2"><label className="text-sm text-zinc-400">Encrypted API key override</label><Input type="password" value={provider.api_key || ''} onChange={(event) => updateProvider('api_key', event.target.value)} placeholder={provider.api_key_set ? 'Stored key present - leave blank to keep' : 'Optional direct key'} className="bg-zinc-800 border-zinc-700" /></div>
            <div className="space-y-2"><label className="text-sm text-zinc-400">Timeout seconds</label><Input type="number" min="5" max="120" value={provider.timeout_seconds ?? 30} onChange={(event) => updateProvider('timeout_seconds', Number(event.target.value))} className="bg-zinc-800 border-zinc-700" /></div>
            <div className="space-y-2"><label className="text-sm text-zinc-400">Max tokens</label><Input type="number" min="128" max="2048" value={provider.max_tokens ?? 600} onChange={(event) => updateProvider('max_tokens', Number(event.target.value))} className="bg-zinc-800 border-zinc-700" /></div>
          </div>
          {provider.api_key_set ? <div className="flex items-center gap-2 text-xs text-zinc-500"><Switch checked={Boolean(provider.clear_api_key)} onCheckedChange={(checked) => updateProvider('clear_api_key', checked)} /><span>Clear the stored key for this provider on save</span></div> : null}
          {purpose === 'ai_guided_trading' && role === 'fallback' ? (
            <div className="flex items-center justify-between rounded-md border border-amber-900/60 bg-amber-950/20 px-4 py-3"><div><div className="text-sm font-medium text-white">Trading-qualified fallback</div><div className="text-xs text-amber-200/70">Required before a local fallback may make an independent wait/entry/hold/exit decision. All TradoPro safety checks still apply.</div></div><Switch checked={Boolean(provider.trading_qualified)} onCheckedChange={(checked) => updateProvider('trading_qualified', checked)} /></div>
          ) : null}
        </div>
        ) : (
          <div className="rounded-md border border-dashed border-zinc-700 bg-zinc-950/30 px-4 py-5 text-sm text-zinc-400">
            Provider details are managed under <span className="font-medium text-white">Market Analysis</span>. Choose <span className="font-medium text-white">Configure independently</span> above to create separate sentiment settings.
          </div>
        )}

        {!inherited ? <div className="space-y-2"><label className="text-sm text-zinc-400">Purpose role prompt</label><textarea value={chain.system_prompt || ''} onChange={(event) => setChain((current) => ({ ...current, system_prompt: event.target.value }))} rows={4} className="w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white" /></div> : null}
        <div className="flex flex-wrap items-center gap-3"><Button onClick={save} disabled={saving || loading}>{saving ? 'Saving...' : inherited ? 'Save Inheritance' : 'Save Provider Chain'}</Button>{!inherited ? <Button variant="outline" onClick={test} disabled={testing || loading}>{testing ? 'Testing...' : `Test ${ROLES[role]}`}</Button> : null}{!inherited ? <span className="text-xs text-zinc-500">Key source: {provider.api_key_source || 'none'}</span> : null}</div>
        {testResult ? <div className={`rounded-md border p-3 text-sm ${testResult.ok ? 'border-emerald-900 bg-emerald-950/40 text-emerald-200' : 'border-red-900 bg-red-950/40 text-red-200'}`}>{testResult.message}{testResult.preview ? <div className="mt-1 text-xs">{testResult.preview}</div> : null}</div> : null}
      </CardContent>
    </Card>
  );
};

export default AIProviderChainManager;
